/**
 * Stacked sidebar panels (L1 | L2 | L3).
 * Scoped to #sidebar-menu.header-sidebar--panels.
 *
 * Desktop: measure real L1 width, pin collapsible translateX to that value,
 * and set collapsible width to 1× or 2× so L2/L3 sit flush (no % gap).
 */
(function () {
  const SIDEBAR_SELECTOR = '#sidebar-menu.header-sidebar--panels';
  const DESKTOP_MQ = '(min-width: 1000px)';

  function init(sidebar) {
    if (!sidebar || sidebar.dataset.panelsBound === 'true') return;
    sidebar.dataset.panelsBound = 'true';

    const collapsible = sidebar.querySelector('header-sidebar-collapsible-panel');
    const mainPanel = sidebar.querySelector('.header-sidebar__main-panel');

    const measurePanelWidth = () => {
      const w = mainPanel?.getBoundingClientRect().width;
      if (w && w > 0) return w;
      const css = getComputedStyle(sidebar).getPropertyValue('--hsp-panel-width').trim();
      const parsed = parseFloat(css);
      return Number.isFinite(parsed) ? parsed : 400;
    };

    const pinDesktopLayout = (depth) => {
      if (!collapsible) return;

      if (!window.matchMedia(DESKTOP_MQ).matches || depth < 2) {
        collapsible.style.transform = '';
        collapsible.style.width = '';
        return;
      }

      const width = measurePanelWidth();
      const flipRaw = getComputedStyle(collapsible).getPropertyValue('--transform-logical-flip').trim();
      const flip = flipRaw === '' ? 1 : Number.parseFloat(flipRaw) || 1;

      collapsible.style.transform = `translateX(${flip * width}px)`;
      collapsible.style.width = depth >= 3 ? `${width * 2}px` : `${width}px`;
    };

    const setDepth = (depth) => {
      sidebar.setAttribute('data-depth', String(depth));
      requestAnimationFrame(() => {
        pinDesktopLayout(depth);
        requestAnimationFrame(() => pinDesktopLayout(depth));
      });
    };

    const closeAllL3 = () => {
      sidebar.querySelectorAll('.header-sidebar__sub-panel--l3').forEach((panel) => {
        panel.hidden = true;
      });
      sidebar.querySelectorAll('[data-open-l3]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });
    };

    const openL3 = (panelId, button) => {
      closeAllL3();
      const panel = document.getElementById(panelId);
      if (!panel) return;
      panel.hidden = false;
      if (button) button.setAttribute('aria-expanded', 'true');
      setDepth(3);

      panel.querySelectorAll('.header-sidebar__back-button, .header-sidebar__linklist li').forEach((el) => {
        el.style.transform = '';
        el.style.opacity = '';
      });
    };

    sidebar.addEventListener('click', (event) => {
      const openBtn = event.target.closest('[data-open-l3]');
      if (openBtn && sidebar.contains(openBtn)) {
        event.preventDefault();
        event.stopPropagation();
        openL3(openBtn.getAttribute('aria-controls'), openBtn);
        return;
      }

      const backL3 = event.target.closest('[data-action="close-l3"]');
      if (backL3 && sidebar.contains(backL3)) {
        event.preventDefault();
        event.stopPropagation();
        closeAllL3();
        setDepth(2);
      }
    });

    if (collapsible) {
      new MutationObserver(() => {
        const active = collapsible.getAttribute('aria-activedescendant');
        closeAllL3();
        setDepth(active ? 2 : 1);
      }).observe(collapsible, { attributes: true, attributeFilter: ['aria-activedescendant'] });
    }

    sidebar.addEventListener('dialog:after-show', () => {
      const depth = Number(sidebar.getAttribute('data-depth') || 1);
      pinDesktopLayout(depth);
    });

    sidebar.addEventListener('dialog:after-hide', () => {
      closeAllL3();
      setDepth(1);
      if (collapsible) {
        collapsible.style.transform = '';
        collapsible.style.width = '';
      }
    });

    window.addEventListener(
      'resize',
      () => {
        const depth = Number(sidebar.getAttribute('data-depth') || 1);
        pinDesktopLayout(depth);
      },
      { passive: true }
    );
  }

  function boot() {
    const sidebar = document.querySelector(SIDEBAR_SELECTOR);
    if (sidebar) init(sidebar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sidebar = event.target.querySelector?.(SIDEBAR_SELECTOR) || document.querySelector(SIDEBAR_SELECTOR);
    if (sidebar) {
      delete sidebar.dataset.panelsBound;
      init(sidebar);
    }
  });
})();
